import React from "react";
import PropTypes from "prop-types";
import { Box, Typography } from "@mui/material";
import { FileSpreadsheet } from "lucide-react";
import { useSelector } from "react-redux";

import { UIEmptyState, UITable } from "../../../../ui";
import messages from "../../../../../assets/locale/messages";
import EventRequirementNameCell from "../EventRequirementNameCell";

import "../../_EventDetailFields.scss";
import "../_RequirementsComments.scss";

const REQ_TABLE_HEADER_PX = 43;
const REQ_TABLE_ROW_PX = 87;
const REQ_TABLE_SX_MAX_SCROLL = {
  maxHeight: "min(70vh, 28rem)",
  overflow: "auto",
};

const EMPTY_STATE_CLASS_NAMES = {
  title: "requirements-comments-empty__title",
  description: "requirements-comments-empty__description",
  iconCircle: "requirements-comments-empty__icon-circle",
};

const EventRequirements = ({ requirements }) => {
  const lang = useSelector((state) => state.locale.lang);
  const dir = lang === "ar" ? "rtl" : "ltr";
  const { events } = messages[lang];
  const t = events.viewEvents;

  const list = requirements ?? [];

  return (
    <Box className="requirements-comments-section">
      <Typography
        component="h2"
        className="requirements-comments-section__heading"
      >
        {t.eventRequirements}
      </Typography>

      {list.length === 0 ? (
        <UIEmptyState
          title={t.emptyRequirementsTitle}
          description={t.emptyRequirementsDescription}
          icon={<FileSpreadsheet size={40} strokeWidth={1.5} />}
          classNames={EMPTY_STATE_CLASS_NAMES}
        />
      ) : (
        <Box sx={REQ_TABLE_SX_MAX_SCROLL}>
          <UITable
            className="requirements-comments-table"
            contentSized
            rows={list.map((req, idx) => ({
              id:
                req?.id != null
                  ? `event-req-${String(req.id)}-${idx}`
                  : `event-req-idx-${idx}`,
              name: req?.requirement?.name ?? req?.name ?? "",
              quantity: req?.quantity ?? "",
              explanation: req?.explanation ?? "",
            }))}
            headerHeight={REQ_TABLE_HEADER_PX}
            rowHeight={REQ_TABLE_ROW_PX}
            pageSize={Math.max(list.length, 1)}
            rowsPerPageOptions={[Math.max(list.length, 1)]}
            columns={[
              {
                field: "name",
                headerName: t.requirementName,
                flex: 1,
                sortable: false,
                renderCell: ({ value }) => (
                  <EventRequirementNameCell name={value} dir={dir} />
                ),
              },
              {
                field: "quantity",
                headerName: t.quantity,
                width: 110,
                align: "center",
                headerAlign: "center",
                sortable: false,
                renderCell: ({ value }) => (
                  <Typography className="event-detail-value" component="span">
                    {value}
                  </Typography>
                ),
              },
              {
                field: "explanation",
                headerName: t.explanation,
                flex: 2,
                sortable: false,
                renderCell: ({ value }) => (
                  <Typography
                    component="p"
                    className="event-detail-table-explanation"
                    sx={{
                      color:
                        "var(--color-foreground-muted, rgba(100, 116, 139, 1)) !important",
                      fontSize: "var(--text-body-sm-font-size)",
                      fontWeight: 400,
                      lineHeight: "var(--text-body-sm-line-height)",
                      margin: 0,
                      whiteSpace: "normal",
                    }}
                  >
                    {value}
                  </Typography>
                ),
              },
            ]}
            striped={false}
            hideFooter
            disableColumnMenu
            sx={{
              "& .MuiDataGrid-cell[data-field='explanation']": {
                alignItems: "center",
                color:
                  "var(--color-foreground-muted, rgba(100, 116, 139, 1)) !important",
                fontWeight: 400,
              },
              "& .MuiDataGrid-cell[data-field='explanation'] .MuiDataGrid-cellContent":
                {
                  overflow: "visible",
                  textOverflow: "unset",
                  whiteSpace: "normal",
                },
              "& .MuiDataGrid-cell[data-field='explanation'] .MuiTypography-root":
                {
                  color:
                    "var(--color-foreground-muted, rgba(100, 116, 139, 1)) !important",
                  fontWeight: 400,
                },
              "& .MuiDataGrid-row": {
                cursor: "default",
                borderBottom: "1px solid var(--color-neutral-200)",
              },
              "& .MuiDataGrid-row:hover, & .MuiDataGrid-row.Mui-hovered": {
                backgroundColor: "var(--color-neutral-0) !important",
              },
              "& .MuiDataGrid-row:last-child": {
                borderBottom: "none",
              },
            }}
          />
        </Box>
      )}
    </Box>
  );
};

EventRequirements.propTypes = {
  requirements: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      name: PropTypes.string,
      quantity: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      explanation: PropTypes.string,
    }),
  ),
};

EventRequirements.defaultProps = {
  requirements: [],
};

export default EventRequirements;
